// src/utils/clozeSelectionHelpers.js
import { tokenizeTarget, normalizeToken } from "./sentenceClozeHelpers";
import { deaccent, baseCandidates } from "../data/encliticForms";

/**
 * Berechnet difficulty_core aus morph_axis, clause_axis und Länge.
 * Wertebereich grob: 0.1 (sehr easy) ... 1.0 (sehr schwer)
 */
export function computeDifficultyCore(sentence) {
  const tokens =
    sentence.tokens || tokenizeTarget(sentence.target || "");
  const length = tokens.length || 1;

  const normalizedLength = Math.min(length / 12, 1); // alles über 12 Wörter = 1
  const morphFactor = (sentence.morph_axis ?? 0) / 3;
  const clauseFactor = (sentence.clause_axis ?? 0) / 3;

  return (
    0.4 * normalizedLength +
    0.3 * morphFactor +
    0.3 * clauseFactor
  );
}

// knownWords-Set ohne Akzente, einmal pro Set berechnet
const plainKnownCache = new WeakMap();
function plainKnownSet(knownWordsSet) {
  let plain = plainKnownCache.get(knownWordsSet);
  if (!plain) {
    plain = new Set([...knownWordsSet].map(deaccent));
    plainKnownCache.set(knownWordsSet, plain);
  }
  return plain;
}

/**
 * Anteil der Tokens, die im knownWords-Set enthalten sind (0..1).
 * Wie im Prüfskript zählen auch Formen mit angehängten Pronomen (hacerlo, dímelo),
 * Gerundien (comiendo) und Wörter mit abweichenden Akzenten als bekannt (encliticForms.js).
 * Französisch (`sentence.lang` = "fr"): Teile wie im Lückentext (j'ai → j' + ai), ohne die
 * spanischen Regeln für angehängte Pronomen.
 */
export function computeKnownRatio(sentence, knownWordsSet) {
  if (!knownWordsSet || knownWordsSet.size === 0) return 0;

  const tokens = tokenizeTarget(sentence.target || "", sentence.lang);
  const spanishForms = !sentence.lang || sentence.lang === "es";
  if (tokens.length === 0) return 0;

  let knownCount = 0;
  for (const token of tokens) {
    const base = normalizeToken(token);
    if (!base) continue;
    if (
      knownWordsSet.has(base) ||
      (spanishForms && baseCandidates(base).some((b) => plainKnownSet(knownWordsSet).has(b)))
    ) {
      knownCount++;
    }
  }

  return knownCount / tokens.length;
}

// Ab diesem Abstand zur Zielschwierigkeit passt ein Satz gar nicht mehr
const DIFFICULTY_SPREAD = 0.4;

/**
 * Wählt einen passenden Satz für die Cloze-Übung:
 *
 * Reihenfolge:
 *  1) known >= minKnownRatio UND difficulty <= maxDifficulty
 *  2) known >= relaxKnownRatio UND difficulty <= maxDifficulty
 *  3) known >= hardMinKnownRatio  (egal wie schwer)
 *  4) letzter Notfall: egal, nimm irgendwas
 *
 * Dadurch fällt die Known-Ratio nur DANN unter hardMinKnownRatio,
 * wenn in der gesamten Sentence-Liste nichts Besseres drin ist.
 *
 * Innerhalb einer Stufe zählt neben der Known-Ratio die Schwierigkeit: mit
 * `targetDifficulty` (siehe getTargetDifficulty in sentencePool.js) die Nähe zu
 * diesem Ziel, sonst gilt leichter = besser. Gezogen wird zufällig aus den 20 besten,
 * so kommen auch etwas leichtere und schwerere Sätze dran.
 */
export function pickSentenceForCloze(
  sentences,
  knownWordsSet,
  options = {}
) {
  if (!sentences || sentences.length === 0) return null;

  const minKnownRatio = options.minKnownRatio ?? 0.8;
  const relaxKnownRatio = options.relaxKnownRatio ?? 0.7;
  const hardMinKnownRatio =
    options.hardMinKnownRatio ?? relaxKnownRatio ?? 0.7;
  const maxDifficulty = options.maxDifficulty ?? 1.0;
  const targetDifficulty =
    options.targetDifficulty == null ? null : Math.min(options.targetDifficulty, maxDifficulty);

  // Alle Sätze mit Kennzahlen anreichern
  const enriched = sentences.map((s) => {
    const difficulty = computeDifficultyCore(s);
    const knownRatio = computeKnownRatio(s, knownWordsSet);
    return { sentence: s, difficulty, knownRatio };
  });

  // Helper: Score berechnen (hohe Known-Ratio + Schwierigkeit nahe am Ziel bzw. leicht)
  const difficultyFit = (difficulty) =>
    targetDifficulty == null
      ? 1 - difficulty
      : 1 - Math.min(1, Math.abs(difficulty - targetDifficulty) / DIFFICULTY_SPREAD);
  const withScore = (arr) =>
    arr.map((c) => {
      const score = 0.6 * c.knownRatio + 0.4 * difficultyFit(c.difficulty); // höher = besser
      return { ...c, score };
    });

  function pickBest(candidates) {
    if (!candidates || candidates.length === 0) return null;
    const scored = withScore(candidates);
    scored.sort((a, b) => b.score - a.score);
    const top = scored.slice(0, Math.min(20, scored.length));
    const idx = Math.floor(Math.random() * top.length);
    return top[idx].sentence;
  }

  // 1) Ideal-Filter: hoher Known-Ratio + Difficulty-Cap
  let candidates = enriched.filter(
    (e) =>
      e.knownRatio >= minKnownRatio &&
      e.difficulty <= maxDifficulty
  );
  let picked = pickBest(candidates);
  if (picked) return picked;

  // 2) Etwas entspannter: relaxKnownRatio + Difficulty-Cap
  candidates = enriched.filter(
    (e) =>
      e.knownRatio >= relaxKnownRatio &&
      e.difficulty <= maxDifficulty
  );
  picked = pickBest(candidates);
  if (picked) return picked;

  // 3) Harte Untergrenze: Known >= hardMinKnownRatio, egal wie schwer
  candidates = enriched.filter(
    (e) => e.knownRatio >= hardMinKnownRatio
  );
  picked = pickBest(candidates);
  if (picked) return picked;

  // 4) Letzter Fallback → alles
  return pickBest(enriched);
}

/**
 * Hilfsfunktion, um aus einem Array von Strings bekannte Wörter zu bauen.
 */
export function buildKnownWordsSetFromStrings(strings) {
  const set = new Set();
  for (const s of strings || []) {
    if (!s) continue;
    const base = normalizeToken(String(s));
    if (base) set.add(base);
  }
  return set;
}
